// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@awc/App/Components/Common/XngZone";
import XngSticky from "@awc/App/Components/XngSticky/XngSticky";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LoadingSpinner } from "@insite/mobius/index";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";

const enum fields {
    isStickyTotal = "isStickyTotal",
    isQuoteDetailsPage = "isQuoteDetailsPage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isStickyTotal]: boolean;
        [fields.isQuoteDetailsPage]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface MyAccountDetailsPagesLayout {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    informationContainer?: InjectableCss;
    productLisContainer?: InjectableCss;
    information?: InjectableCss;
    total?: InjectableCss;
    productList?: InjectableCss;
    xngStickyStyles?: InjectableCss;
    spinnerWrapper?: InjectableCss;
    details?: InjectableCss;
    detailsLeftColumn?: InjectableCss;
    detailsRightColumn?: InjectableCss;
    titleActionWrapper?: InjectableCss;
    actionContainer?: InjectableCss;
}

export const myAccountDetailsPagesLayout: MyAccountDetailsPagesLayout = {};

const styles = myAccountDetailsPagesLayout;
const MyAccountDetailsPagesLayout: FC<Props> = ({ id, fields, quoteState }) => {
    return (
        <StyledWrapper {...styles.container} data-test-selector="My-Account-Details-Pages-Layout">
            <StyledWrapper {...styles.titleActionWrapper}>
                <StyledWrapper {...styles.titleContainer}>
                    <XngZone contentId={id} zoneName="Content01" hint="Zone For Title" />
                </StyledWrapper>
                <StyledWrapper {...styles.actionContainer}>
                    <XngZone contentId={id} zoneName="Content09" hint="Zone For Actions" />
                </StyledWrapper>
            </StyledWrapper>

            {!getIsShareEntity() && !quoteState.value && fields.isQuoteDetailsPage ? (
                <StyledWrapper {...styles.spinnerWrapper}>
                    <LoadingSpinner />
                </StyledWrapper>
            ) : (
                <>
                    <StyledWrapper {...styles.informationContainer}>
                        <StyledWrapper {...styles.productList}>
                            <StyledWrapper {...styles.details}>
                                <StyledWrapper {...styles.detailsLeftColumn}>
                                    <XngZone contentId={id} zoneName="Content07" hint="Zone For Details" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.detailsRightColumn}>
                                    <XngZone contentId={id} zoneName="Content08" hint="Zone For Details" />
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.information}>
                                <XngZone contentId={id} zoneName="Content03" hint="Zone For Information" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.productList}>
                                <XngZone contentId={id} zoneName="Content06" hint="Zone Product List Not Full Width" />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.total}>
                            {fields.isStickyTotal ? (
                                <XngSticky {...styles.xngStickyStyles}>
                                    <XngZone contentId={id} zoneName="Content04" hint="Zone For Total" />
                                </XngSticky>
                            ) : (
                                <>
                                    <XngZone contentId={id} zoneName="Content04" hint="Zone For Total" />
                                </>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.productLisContainer}>
                        <XngZone contentId={id} zoneName="Content05" hint="Zone Product List" />
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(MyAccountDetailsPagesLayout),
    definition: {
        group: "Layout" as any,
        displayName: "My Account Details Pages Layout",
        fieldDefinitions: [
            {
                name: fields.isStickyTotal,
                displayName: "Is Sticky Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.isQuoteDetailsPage,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
